import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef, ReactNode } from 'react';
import { weatherService } from '../services/weatherService';

export type WeatherCondition = 'Clear' | 'Cloudy' | 'Rain' | 'Snow' | 'Storm' | 'Fog' | 'Unknown';

interface WeatherState {
  condition: WeatherCondition;
  temperature: number | null;
  description: string;
  lastUpdated: number;
  isNight: boolean;
  moonPhase: string;
  moonIllumination: number;
}

interface WeatherContextType {
  weather: WeatherState;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  updateCondition: (condition: WeatherCondition) => void;
}

const WeatherContext = createContext<WeatherContextType | undefined>(undefined);

export const WeatherProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [weather, setWeather] = useState<WeatherState>({
    condition: 'Unknown',
    temperature: null,
    description: '',
    lastUpdated: 0,
    isNight: false,
    moonPhase: '',
    moonIllumination: 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isFetching = useRef(false);

  const isInitialLoad = useRef(true);

  const fetchWeather = useCallback(async () => {
    // Avoid simultaneous fetches
    if (isFetching.current) return;
    
    isFetching.current = true;
    // Only show loading on initial fetch to avoid UI flashing
    if (isInitialLoad.current) {
      setLoading(true);
    }
    
    setError(null);
    try {
      const data = await weatherService.getCurrentWeather();
      if (data) {
        setWeather(prev => {
          // Check if data is truly different to avoid unnecessary re-renders
          const hasChanged = prev.condition !== data.condition || 
                             prev.temperature !== data.temperature ||
                             prev.isNight !== data.isNight ||
                             prev.description !== data.description;
          
          // If nothing changed and we already have data, don't update state to avoid re-renders
          if (!hasChanged && prev.condition !== 'Unknown') {
            return prev;
          }
          
          return {
            condition: data.condition as WeatherCondition,
            temperature: data.temperature,
            description: data.description,
            lastUpdated: Date.now(),
            isNight: data.isNight,
            moonPhase: data.moonPhase,
            moonIllumination: data.moonIllumination,
          };
        });
        isInitialLoad.current = false;
      } else {
        setError('Unable to fetch weather data');
      }
    } catch (err) {
      setError('Failed to load weather information');
      console.error('Weather fetch error:', err);
    } finally {
      setLoading(false);
      isFetching.current = false;
    }
  }, []);




  useEffect(() => {
    fetchWeather();
    
    // Default to 30 minutes if not specified, but enforce minimum of 1 minute
    const config = weatherService.getConfig();
    const intervalMinutes = Math.max(config.updateInterval || 30, 1);
    const intervalMs = intervalMinutes * 60 * 1000;
    
    const interval = setInterval(fetchWeather, intervalMs);
    return () => clearInterval(interval);
  }, [fetchWeather]);


  const updateCondition = useCallback((condition: WeatherCondition) => {
    setWeather(prev => ({ ...prev, condition }));
  }, []);

  const value = useMemo(() => ({
    weather,
    loading,
    error,
    refresh: fetchWeather,
    updateCondition
  }), [weather, loading, error, fetchWeather, updateCondition]);

  return (
    <WeatherContext.Provider value={value}>
      {children}
    </WeatherContext.Provider>
  );
};

export const useWeather = () => {
  const context = useContext(WeatherContext);
  if (context === undefined) {
    throw new Error('useWeather must be used within a WeatherProvider');
  }
  return context;
};

